import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/registracija")({
  head: () => {
    const url = "https://kupish.si/registracija";
    const title = "Registracija — kupish.si";
    const description = "Ustvarite brezplačen račun na kupish.si — kupujte izdelke različnih ponudnikov, shranjujte priljubljene izdelke ali odprite lastno spletno trgovino.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: Page,
});

function Page() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate({ to: "/dashboard", replace: true });
  }, [user, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/potrdi-email`,
        data: { first_name: firstName, last_name: lastName },
      },
    });
    setLoading(false);
    if (error) setError(error.message);
    else setSent(true);
  }

  async function onGoogle() {
    setError(null);
    setLoading(true);
    const res = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
      extraParams: { prompt: "select_account" },
    });
    if (res.error) {
      setError(res.error.message);
      setLoading(false);
      return;
    }
    if (res.redirected) return;

    const { data, error: userError } = await supabase.auth.getUser();
    if (userError || !data.user) {
      setError("Google prijava je uspela, vendar se seja ni shranila. Poskusite znova.");
      setLoading(false);
      return;
    }
    navigate({ to: "/dashboard", replace: true });
  }

  return (
    <section className="mx-auto max-w-md px-6 py-24">
      <div className="eyebrow">Pridružite se</div>
      <h1 className="font-serif text-5xl mt-3">Registracija</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Ustvarite račun in postanite del kupish.si.
      </p>

      {sent ? (
        <div className="mt-10 border border-border p-6 text-sm">
          <div className="font-serif text-xl mb-2">Preverite e-pošto</div>
          <p className="text-muted-foreground">
            Poslali smo vam povezavo za potrditev na <strong>{email}</strong>.
            Kliknite jo za dokončanje registracije.
          </p>
        </div>
      ) : (
        <>
          <button type="button" onClick={onGoogle} disabled={loading}
            className="mt-8 w-full border border-border py-3 text-sm hover:bg-muted flex items-center justify-center gap-2">
            <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84c-.21 1.13-.85 2.08-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.63z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.91-2.26c-.81.54-1.83.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A8.99 8.99 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.96 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.28-1.71V4.96H.96A8.99 8.99 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A8.99 8.99 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z"/></svg>
            Nadaljuj z Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex-1 h-px bg-border" /> ali <div className="flex-1 h-px bg-border" />
          </div>

          <form className="space-y-5" onSubmit={onSubmit}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs eyebrow">Ime</label>
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
              </div>
              <div>
                <label className="text-xs eyebrow">Priimek</label>
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
              </div>
            </div>
            <div>
              <label className="text-xs eyebrow">E-mail</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
            </div>
            <div>
              <label className="text-xs eyebrow">Geslo</label>
              <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
            </div>
            {error && <div className="text-sm text-destructive">{error}</div>}
            <button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground py-3 text-sm hover:opacity-90 disabled:opacity-60">
              {loading ? "Ustvarjam…" : "Ustvari račun"}
            </button>
          </form>
        </>
      )}

      <div className="mt-6 text-sm text-muted-foreground text-center">
        Že imate račun? <Link to="/prijava" className="underline underline-offset-4 hover:text-accent">Prijavite se</Link>
      </div>
    </section>
  );
}
